import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_PROGRAMA_CEAO_MEDICO, { adaptAddVisitaCEAOMedicoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaCEAOMedico";
import { useStepSave } from "../../../../hooks/useStepSave";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import CardBox from "../../CardBox/CardBox";
import { IndicacionesVisitaInput } from "./components/IndicacionesVisitaInput";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { GET_SOLICITUD_INDICACION_PENDIENTE } from "../../../../../../data/graphQL/queries/GestionAsistencial/getSolicitudIndicacionPendiente";

export default function ProgramaCEAO({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, esUrgencia }) {
  const [formData, setFormData] = useState({});
  const { visita, setVisita } = useVisita();
  const [cambioIndicacion, setCambioIndicacion] = useState(false);
  const [solicitudIndicacionPendiente, setSolicitudIndicacionPendiente] = useState(null);

  const [getSolicitudIndicacionPendiente, { data: dataSolicitudPendiente }] = useLazyQuery(GET_SOLICITUD_INDICACION_PENDIENTE, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataSolicitudPendiente) return;
    setSolicitudIndicacionPendiente(dataSolicitudPendiente.solicitudIndicacionPendiente.success && dataSolicitudPendiente.solicitudIndicacionPendiente.valorEsperado != null);
  }, [dataSolicitudPendiente]);

  const [save] = useMutation(SAVE_PROGRAMA_CEAO_MEDICO);

  useEffect(() => {
    const visitaIndicaciones = visita?.listaIndicacion || [];
    const formIndicaciones = formData?.listaIndicacion || [];

    if (visitaIndicaciones.length !== formIndicaciones.length) {
      setCambioIndicacion(true);
      return;
    }

    const formMap = new Map(
      formIndicaciones.map(ind => [ind.idInsumo, ind.cantidadDiaria])
    );

    const hayCambios = visitaIndicaciones.some(visitaInd => {
      const formCantidad = formMap.get(visitaInd.idInsumo);
      // Si no existe el insumo o la cantidad es diferente
      return formCantidad === undefined || formCantidad !== visitaInd.cantidadDiaria;
    });

    setCambioIndicacion(hayCambios);
  }, [visita?.listaIndicacion, formData?.listaIndicacion]);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        // Reglas de visibilidad / obligatoriedad
        switch (input.name) {
          case "justificacionModificacion":
            newInput.required = cambioIndicacion;
            break;

          case "medicacionAnticonvulsivanteTexto":
            newInput.hidden = !(Number(formData?.cantidadMedicacionAnticonvulsivante) > 0);
            newInput.required = Number(formData?.cantidadMedicacionAnticonvulsivante) > 0;
            break;

          case "resultadoCetonemia":
            newInput.hidden = formData?.cetonemia !== true;
            newInput.required = formData?.cetonemia === true;
            break;

          case "resultadoCetonuria":
            newInput.hidden = formData?.cetonuria !== true;
            newInput.required = formData?.cetonuria === true;
            break;

          case "suplemento":
            newInput.hidden = formData?.suplementoVitaminico !== true;
            newInput.required = formData?.suplementoVitaminico === true;
            break;

          case "indicacionSuplemento":
            newInput.hidden = formData?.suplementoVitaminico !== true;
            newInput.required = formData?.suplementoVitaminico === true;
            break;

          case "observacion":
            newInput.hidden = !(Number(formData?.cantidadCrisis) > 0);
            newInput.required = Number(formData?.cantidadCrisis) > 0;
            break;
          case "complicaciones":
            const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
            newInput.options = complicacionesCombo?.map((opt) => {
              if (Array.isArray(formData.complicaciones)) {
                const hasNinguna = formData.complicaciones.includes(ningunaId);
                const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
                if (hasNinguna) {
                  // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
                  return { ...opt, disabled: opt.id !== ningunaId };
                }
                if (hasOther) {
                  // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
                  return { ...opt, disabled: opt.id === ningunaId };
                }
              }
              return { ...opt, disabled: false };
            });
            break;
          default:
            break;
        }

        if (readOnly) newInput.disabled = true;
        if (esUrgencia && input.name !== "justificacionModificacion") newInput.required = false;

        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [readOnly, formData, complicacionesCombo, cambioIndicacion, esUrgencia]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const indicaciones = Array.isArray(formData.listaIndicacion) ? formData.listaIndicacion : [];
    let totalCalorias = 0;

    indicaciones.forEach((indicacion) => {
      const cantidad = Number(indicacion.cantidadDiaria) || 0;
      const calorias = Number(indicacion.calorias) || 0;
      totalCalorias += (cantidad * calorias);
    });

    setFormData((prev) => ({ ...prev, caloriasSO: totalCalorias }));
  }, [formData.listaIndicacion]);

  useEffect(() => {
    setFormData((prev) => {
      const req = Number(prev.caloriasTotales);
      const cub = Number(prev.caloriasSO);
      const pct = req > 0 && !Number.isNaN(cub) ? Math.round(cub / req * 100) : null;

      if (pct === prev.porcentajeSO) return prev;
      return { ...prev, porcentajeSO: pct };
    });
  }, [formData.caloriasTotales, formData.caloriasSO]);

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });
    const { data } = await save({ variables: { input: adaptAddVisitaCEAOMedicoInput(formData) } });

    const idVisitaCeao = data?.saveVisitaCeaoMedico?.idVisitaCeao;
    if (!idVisitaCeao) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaCeao }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);

      setFormData(prev => ({
        ...filterVisitaData(visita, inputNames),
        idVisitaCeao: visita.idVisitaCeao,
        idVisita: visita.idVisita,
        caloriasSO: visita.caloriasSO || prev.caloriasSO
      }));
    }
  }, [visita]);

  useEffect(() => {
    if (!visita?.idEvaluacion) return;

    getSolicitudIndicacionPendiente({ variables: { idEvaluacion: visita?.idEvaluacion } });
  }, [visita?.idEvaluacion]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaCEAO",
      stepName: "Programa CEAO",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    {input.name !== "listaIndicacion" ?
                      <RenderInputByTypeMobile
                        input={input}
                        value={formData[input.name]}
                        handleChange={handleChange}
                        row={input.type === "multiple_boolean"} /> :


                      <IndicacionesVisitaInput
                        readOnly={readOnly}
                        handleChange={handleChange}
                        indicaciones={formData[input.name]}
                        inputName={input.name}
                        idEvaluacion={visita?.idEvaluacion}
                        solicitudIndicacionPendiente={solicitudIndicacionPendiente}
                      />
                    }
                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "Indicaciones",
    inputs: [
      {
        name: "listaIndicacion",
        displayName: "Indicaciones (productos)",
        type: "custom",
        required: true
      },
      {
        name: "justificacionModificacion",
        displayName: "Justificación por modificación",
        type: "long_string",
        required: true,
        maxLength: 4000
      },
      {
        name: "indicacionComentario",
        displayName: "Comentarios de indicación",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "suplementoVitaminico",
        displayName: "Suplementos vitamínicos",
        type: "boolean",
        lightTitle: true
      },
      {
        name: "suplemento",
        displayName: "Suplementos",
        type: "string",
        required: true,
        maxLength: 1000
      },
      {
        name: "indicacionSuplemento",
        displayName: "Indicación de Suplementos",
        type: "long_string",
        required: true,
        maxLength: 1500
      }]

  },
  {
    title: "Requerimientos",
    inputs: [
      {
        name: "caloriasTotales",
        displayName: "Requerimiento calórico total",
        required: true,
        type: "float", //Se completa manualmente
      },
      {
        name: "caloriasSO",
        displayName: "Calorías cubiertas por SO (totales)",
        type: "float",
        required: true, //Calculo: sumatoria de (calorias de producto * cantidad diaria) de todas las indicaciones, posibilidad de edicion
      },
      {
        name: "porcentajeSO",
        displayName: "%SO",
        type: "int",
        required: true, //Calculo: (caloriasSO / caloriasTotales) * 100, posibilidad de edicion
      }]

  },
  {
    title: "",
    inputs: [
      {
        name: "cantidadAnticonvulsivante",
        displayName: "Cantidad medicación anticonvulsivante",
        type: "int",
        required: true,
        min: 0,
        max: 10
      },
      {
        name: "medicacionAnticonvulsivante",
        displayName: "Medicación anticonvulsivante",
        type: "long_string",
        required: true,
        maxLength: 1000
      },
      {
        name: "cetonemia",
        displayName: "Cetonemia",
        type: "boolean",
        required: true,
        lightTitle: true
      },
      {
        name: "resultadoCetonemia",
        displayName: "Resultado",
        type: "string",
        required: true,
        maxLength: 200
      },
      {
        name: "cetonuria",
        displayName: "Cetonuria",
        type: "boolean",
        required: true,
        lightTitle: true
      },
      {
        name: "resultadoCetonuria",
        displayName: "Resultado",
        type: "string",
        required: true,
        maxLength: 200
      },
      {
        name: "cantidadCrisis",
        displayName: "Cantidad de crisis diarias",
        type: "int",
        required: true,
        min: 0,
        max: 100
      },
      {
        name: "observacion",
        displayName: "Observación",
        type: "long_string",
        required: true,
        maxLength: 1500
      },
      {
        name: "complicaciones",
        displayName: "Complicaciones",
        type: "multiple_select",
        required: true
      }]

  }];